 /* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { Clock, CheckCircle, Tray, ArrowsClockwise } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

// Render a "5m ago" / "2 days ago" string from an ISO timestamp.
function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "yesterday" : `${d} days ago`;
}

const DEFAULT_LIMIT = 5;
const EXPANDED_LIMIT = 20;

export default function ReviewQueueCard() {
  const [items, setItems] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const load = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/review-queue", { params: { limit } });
      setItems(data.items || []);
      setTotalPending(data.total_pending || 0);
    } catch (e) {
      console.error("Failed to load review queue", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [limit]);

  const verify = async (it) => {
    const key = `${it.user_id}-${it.week_num}-${it.task_index}`;
    setBusyKey(key);
    try {
      await api.post("/admin/progress/verify", {
        user_id: it.user_id,
        week_num: it.week_num,
        task_index: it.task_index,
        verified: true,
      });
      // Optimistically remove the row so the next one slides up
      // without a network round-trip lag.
      setItems((prev) => prev.filter((x) => !(
        x.user_id === it.user_id
        && x.week_num === it.week_num
        && x.task_index === it.task_index
      )));
      setTotalPending((n) => Math.max(0, n - 1));
      toast.success(`Verified · ${it.student_name}`);
      // Re-fetch in the background to top up the list when we're near empty.
      if (items.length <= 3) load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
      load(); // refresh to reconcile state
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="review-queue-card">
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Tray weight="fill" className="text-amber-500 shrink-0" size={22} />
          <h3 className="font-outfit font-bold text-xl">Review queue</h3>
          {totalPending > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-100 text-amber-900 text-xs font-bold tracking-wider uppercase"
              data-testid="review-queue-total"
            >
              {totalPending} pending
            </span>
          )}
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="review-queue-refresh-btn"
          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50"
          title="Refresh"
        >
          <ArrowsClockwise size={16} weight="bold" className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="review-queue-loading">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-slate-400" data-testid="review-queue-empty">
          <CheckCircle size={36} weight="duotone" className="mx-auto mb-2 text-emerald-300" />
          <p className="text-sm">All caught up — no pending submissions.</p>
        </div>
      ) : (
        <>
          <ul className="space-y-2" data-testid="review-queue-list">
            <AnimatePresence initial={false}>
              {items.map((it) => {
                const key = `${it.user_id}-${it.week_num}-${it.task_index}`;
                const isBusy = busyKey === key;
                return (
                  <motion.li
                    key={key}
                    layout
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 80, height: 0, marginTop: 0, marginBottom: 0 }}
                    transition={{ duration: 0.22 }}
                    data-testid={`review-queue-item-${key}`}
                    className="flex items-start gap-3 p-3 rounded-2xl border border-amber-200 bg-amber-50/60"
                  >
                    <Clock size={18} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-outfit font-bold text-sm truncate">
                          {it.student_name}
                        </span>
                        <span className="text-[10px] font-bold tracking-wider uppercase text-slate-500">
                          Wk {it.week_num} · {it.week_title}
                        </span>
                      </div>
                      <p className="text-sm text-slate-800 mt-0.5 line-clamp-2">{it.task_text}</p>
                      <div className="text-[11px] text-slate-500 mt-1">
                        Submitted {timeAgo(it.submitted_at)}
                      </div>
                    </div>
                    <button
                      onClick={() => verify(it)}
                      disabled={isBusy}
                      data-testid={`review-queue-verify-${key}`}
                      className="shrink-0 self-center btn-tactile-yellow inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-500 text-white font-bold text-xs hover:bg-emerald-600 disabled:opacity-60"
                    >
                      <CheckCircle size={14} weight="fill" />
                      {isBusy ? "..." : "Verify"}
                    </button>
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>

          {totalPending > items.length && limit === DEFAULT_LIMIT && (
            <button
              onClick={() => setLimit(EXPANDED_LIMIT)}
              data-testid="review-queue-show-more-btn"
              className="mt-3 w-full text-center text-xs font-semibold text-slate-600 hover:text-slate-900 py-2 rounded-lg hover:bg-slate-50"
            >
              Show more ({totalPending - items.length} more pending)
            </button>
          )}
          {limit > DEFAULT_LIMIT && items.length > DEFAULT_LIMIT && (
            <button
              onClick={() => setLimit(DEFAULT_LIMIT)}
              data-testid="review-queue-show-less-btn"
              className="mt-3 w-full text-center text-xs font-semibold text-slate-600 hover:text-slate-900 py-2 rounded-lg hover:bg-slate-50"
            >
              Show fewer
            </button>
          )}
        </>
      )}
    </div>
  );
}
